import { useCallback } from 'react'

import {
  ApproveMatchWithPostingsPayload,
  useApproveMatchWithPostings,
} from '@features/paymentReconciliationModal/hooks/useApproveMatchWithPostings'
import { useCreateSubjectsAndBankLinesReconciliation } from '@features/paymentReconciliationModal/hooks/useReconcileSubjectsAndBankLines'
import { CreateSubjectsAndBankLinesReconcilationOptions } from '@features/paymentReconciliationModal/query-api'
import { PostingType } from '@features/paymentReconciliationModal/types/postingType'
import { BankMatchUiFlowValue, UiSourceValue } from '@features/reconciliation/enums'
import { BankLine, DifferenceType, ReconciliationSuggestion } from '@modules-deprecated/bankReconciliation/types'

import { queryClient } from '../../../config/queryClient'
import { BookkeepingItem } from '../types/bookkeepingItem'
import { convertBookkeepingItemsToReconciliationSubjects } from '../utils/convertBookkeepingItemsToReconciliationSubjects'
import { invalidateReconciliationQueries } from '../utils/invalidateReconciliationQueries'

interface UseAcceptSuggestionProps {
  onSuccess?: () => void
  onError?: (errorMessage?: string) => void
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

interface AcceptSuggestionParams {
  bankLine: BankLine | null
  suggestedBookkeepingItem?: BookkeepingItem
  matchSuggestion?: ReconciliationSuggestion
  differenceType: DifferenceType | null
}

export const useAcceptSuggestion = (props: UseAcceptSuggestionProps) => {
  const { onSuccess, onError, uiSource, uiFlow } = props || {}

  const { create: acceptPayableSuggestion, isProcessing: isAcceptingPayable } =
    useCreateSubjectsAndBankLinesReconciliation({
      onSuccess: () => {
        invalidateReconciliationQueries(queryClient)
        onSuccess?.()
      },
      onError: (error) => {
        onError?.(error?.message)
      },
    })
  const { approve: acceptPostingSuggestion, isProcessing: isAcceptingPosting } = useApproveMatchWithPostings({
    onSuccess: () => {
      invalidateReconciliationQueries(queryClient)
      onSuccess?.()
    },
    onError: (error) => {
      onError?.(error?.message)
    },
    uiSource,
    uiFlow,
  })

  const handlePayableSuggestion = useCallback(
    ({ suggestedBookkeepingItem, matchSuggestion, differenceType }: AcceptSuggestionParams) => {
      if (matchSuggestion && suggestedBookkeepingItem) {
        const crossCurrency = matchSuggestion.reconcilables[0].currency !== matchSuggestion.transactions[0].currency
        const payload: CreateSubjectsAndBankLinesReconcilationOptions = {
          organizationId: matchSuggestion.reconcilables[0].organization_id,
          bankLinesIds: [matchSuggestion.transactions[0].id],
          subjects: convertBookkeepingItemsToReconciliationSubjects([suggestedBookkeepingItem]),
          parameters: {
            isAutoMatch: true,
            expectedAmount: crossCurrency ? Math.abs(matchSuggestion.reconcilables[0].amount) : undefined,
            differenceType: crossCurrency ? undefined : (differenceType ?? undefined),
          },
          uiSource,
          uiFlow,
        }

        acceptPayableSuggestion(payload)
      }
    },
    [acceptPayableSuggestion, uiSource, uiFlow],
  )

  const handlePostingSuggestion = useCallback(
    ({ bankLine, suggestedBookkeepingItem, matchSuggestion }: AcceptSuggestionParams) => {
      if (bankLine?.match && matchSuggestion && suggestedBookkeepingItem) {
        const payload: ApproveMatchWithPostingsPayload = {
          matchId: bankLine.match.id,
          isAutoMatch: true,
          subjects: [{ id: suggestedBookkeepingItem.id, type: PostingType.Posting }],
        }

        acceptPostingSuggestion(payload)
      }
    },
    [acceptPostingSuggestion],
  )

  const acceptSuggestion = useCallback(
    ({ bankLine, suggestedBookkeepingItem, matchSuggestion, differenceType }: AcceptSuggestionParams) => {
      if (suggestedBookkeepingItem?.type === 'posting') {
        handlePostingSuggestion({ bankLine, suggestedBookkeepingItem, matchSuggestion, differenceType })
      } else {
        handlePayableSuggestion({ bankLine, suggestedBookkeepingItem, matchSuggestion, differenceType })
      }
    },
    [handlePostingSuggestion, handlePayableSuggestion],
  )

  return {
    isAccepting: isAcceptingPayable || isAcceptingPosting,
    acceptSuggestion,
  }
}
